import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { patientsApi } from '../api/patients.api';
import { patientsQueryKey } from './usePatients';
import type { Patient, PatientRefresh } from '../types/patient.types';

export const patientRefreshQueryKey = ['patients', 'refresh'] as const;

/**
 * Merges a `GetPatientRefresh` result into a cached patient list: the primary
 * patient takes the fresh `attachedBy` and (when the server sent one) the
 * fresh attach code; dependents are untouched. Returns the input unchanged
 * when there is no primary patient to patch (TanStack keeps the cache as-is
 * when the updater returns `undefined`).
 */
export function applyPatientRefresh(
  patients: Patient[] | undefined,
  fresh: PatientRefresh,
): Patient[] | undefined {
  if (!patients?.some((p) => p.isPrimary)) return patients;
  return patients.map((p) =>
    p.isPrimary
      ? { ...p, attachedBy: fresh.attachedBy, actCode: fresh.uniqueCode ?? p.actCode }
      : p,
  );
}

/**
 * Re-reads the primary patient's attach code + the accounts it is attached to
 * (`GET /v2/GetPatientRefresh`) on EVERY mount — i.e. every entry to the
 * patients page — and writes the result into the cached `['patients']` list,
 * so the "Conturi la care ești atașat" block and the attach-code dialog show
 * the live values without reloading the whole list.
 *
 * Fallback: when the call fails (network, 5xx, no ZUMO token…) or the server
 * has nothing to say (204), the cached list stays exactly as it was — the page
 * keeps working from cache the way it did before this endpoint existed.
 *
 * `gcTime: 0` + `refetchOnMount: 'always'` make every page entry a fresh call
 * while a dialog opened on the same page does not trigger another one.
 */
export function usePatientRefresh() {
  const queryClient = useQueryClient();
  const refresh = useQuery({
    queryKey: patientRefreshQueryKey,
    queryFn: patientsApi.refreshPatient,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });

  const fresh = refresh.data ?? null;
  useEffect(() => {
    if (!fresh) return;
    queryClient.setQueryData<Patient[]>(patientsQueryKey, (prev) =>
      applyPatientRefresh(prev, fresh),
    );
  }, [fresh, queryClient]);

  return {
    /** True while the live values are being fetched (the cached ones are shown meanwhile). */
    isRefreshing: refresh.isFetching,
    /** True when the last attempt failed and the page is showing cached values. */
    isStale: refresh.isError,
  };
}
